import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Users, Key, Loader2, Shield, UserX, Trash2, UserCheck, Plus, Settings, LayoutGrid, List, Eye } from 'lucide-react';
import { useEscapeToHome } from '@/hooks/useKeyboard';
import { useAuth } from '@/contexts/AuthContext';

export default function AdminPage() {
  useEscapeToHome();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ username: '', password: '', display_name: '', role: 'user', assigned_project_ids: [] });
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignSelection, setAssignSelection] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // list | board
  const [groupBy, setGroupBy] = useState('workspace'); // workspace | role | tree

  // 'v' toggles view, 'g' toggles group-by (workspace → role → tree)
  useEffect(() => {
    const h = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const isTyping = ['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName) || e.target.getAttribute('role') === 'combobox';
      if (e.key === 'v' && !isTyping) { e.preventDefault(); setViewMode(m => m === 'board' ? 'list' : 'board'); }
      else if (e.key === 'g' && !isTyping) { e.preventDefault(); setGroupBy(g => g === 'workspace' ? 'role' : g === 'role' ? 'tree' : 'workspace'); }
    };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, []);

  const viewAs = async (userId) => {
    try {
      const { data } = await api.post(`/super-admin/view-as/${userId}`);
      sessionStorage.setItem('view_as_token', data.access_token);
      sessionStorage.setItem('view_as_original_token', localStorage.getItem('token'));
      sessionStorage.setItem('view_as_target_username', data.user.username);
      localStorage.setItem('token', data.access_token);
      toast.success(`Now viewing as @${data.user.username}`);
      window.location.href = '/';
    } catch (e) { toast.error(e.response?.data?.detail || 'View As failed'); }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [u, p] = await Promise.all([api.get('/admin/users'), api.get('/projects')]);
      setUsers(u.data.users);
      setProjects(p.data.projects);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    try {
      await api.post(`/admin/reset-password/${resetTarget.id}`, { new_password: newPassword });
      toast.success('Password reset successfully');
      setResetTarget(null); setNewPassword('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const toggleDeactivate = async (userId) => {
    try { const { data } = await api.put(`/admin/users/${userId}/deactivate`); toast.success(data.message); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteUser = async (userId) => {
    try { await api.delete(`/admin/users/${userId}`); toast.success('User deleted'); setConfirmAction(null); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const createUser = async () => {
    const { username, password, role } = createForm;
    if (!username.trim() || password.length < 6) { toast.error('Username + password (min 6 chars) required'); return; }
    try {
      await api.post('/admin/users', { ...createForm, username: createForm.username.trim().toLowerCase() });
      toast.success('User created');
      setCreateOpen(false);
      setCreateForm({ username: '', password: '', display_name: '', role: 'user', assigned_project_ids: [] });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const openAssign = (u) => { setAssignTarget(u); setAssignSelection(u.assigned_project_ids || []); };
  const saveAssign = async () => {
    try {
      await api.put(`/admin/users/${assignTarget.id}/assignments`, { assigned_project_ids: assignSelection });
      toast.success('Assignments updated');
      setAssignTarget(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const toggleAssign = (pid) => setAssignSelection(prev => prev.includes(pid) ? prev.filter(x => x !== pid) : [...prev, pid]);

  const topLevel = projects.filter(p => !p.parent_project_id);
  const subsByParent = {}; projects.forEach(p => { if (p.parent_project_id) (subsByParent[p.parent_project_id] = subsByParent[p.parent_project_id] || []).push(p); });
  const projectMap = {}; projects.forEach(p => { projectMap[p.id] = p.name; });

  const roleColor = (r) => r === 'super_admin' ? 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300' : r === 'admin' ? 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300' : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300';

  return (
    <div className="p-4 md:p-8 w-full" data-testid="admin-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl sm:text-3xl font-medium tracking-tight flex items-center gap-2"><Users className="h-6 w-6" /> User Management</h2>
          <div className="text-sm text-zinc-500 mt-1 flex items-center gap-1">{users.length} user(s) · Logged in as <Badge className={roleColor(me?.role)}>{me?.role || 'user'}</Badge></div>
        </div>
        <div className="flex items-center gap-2">
          {/* Group by toggle */}
          <div className="hidden sm:flex items-center gap-1 text-xs text-zinc-400 mr-2">
            <span>Group:</span>
            <button onClick={() => setGroupBy('workspace')} className={`px-2 py-1 rounded ${groupBy === 'workspace' ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 font-medium' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'}`} data-testid="admin-group-workspace">Workspace</button>
            <button onClick={() => setGroupBy('role')} className={`px-2 py-1 rounded ${groupBy === 'role' ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 font-medium' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'}`} data-testid="admin-group-role">Role</button>
            <button onClick={() => setGroupBy('tree')} className={`px-2 py-1 rounded ${groupBy === 'tree' ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100 font-medium' : 'hover:bg-zinc-100 dark:hover:bg-zinc-800'}`} data-testid="admin-group-tree">Tree</button>
          </div>
          <div className="flex bg-zinc-100 dark:bg-zinc-800 rounded-lg p-0.5">
            <button onClick={() => setViewMode('board')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'board' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="admin-view-board">
              <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.5} /> Board</button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${viewMode === 'list' ? 'bg-white dark:bg-zinc-700 shadow-sm text-zinc-900 dark:text-zinc-100' : 'text-zinc-500'}`} data-testid="admin-view-list">
              <List className="h-3.5 w-3.5" strokeWidth={1.5} /> List</button>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="rounded-xl" data-testid="create-user-btn"><Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> New User</Button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-zinc-400" /></div>
       : users.length === 0 ? <p className="text-center text-zinc-500 py-10">No users</p>
       : viewMode === 'board' ? (
          (() => {
            // Build columns based on groupBy
            let colEntries = [];

            if (groupBy === 'role') {
              const roleGroups = { super_admin: { label: 'Super Admin', items: [] }, admin: { label: 'Admin', items: [] }, user: { label: 'User', items: [] } };
              users.forEach(u => { (roleGroups[u.role] || roleGroups.user).items.push(u); });
              colEntries = Object.entries(roleGroups).filter(([,c]) => c.items.length > 0);
            } else if (groupBy === 'tree') {
              // Hierarchical: top-level workspace → sub-workspaces with users assigned to each
              const cols = {};
              topLevel.forEach(p => { cols[p.id] = { label: p.name, items: [], subs: [] }; });
              const all = { label: 'All (Super Admin)', items: [] };
              const unassigned = { label: 'Unassigned', items: [] };
              users.forEach(u => {
                if (u.role === 'super_admin') { all.items.push(u); return; }
                const assigned = u.assigned_project_ids || [];
                if (assigned.length === 0) { unassigned.items.push(u); return; }
                assigned.forEach(pid => {
                  const proj = projects.find(x => x.id === pid);
                  if (!proj) return;
                  if (proj.parent_project_id) {
                    const root = cols[proj.parent_project_id];
                    if (root) {
                      let sub = root.subs.find(s => s.id === pid);
                      if (!sub) { sub = { id: pid, label: proj.name, items: [] }; root.subs.push(sub); }
                      if (!sub.items.find(x => x.id === u.id)) sub.items.push(u);
                    }
                  } else if (cols[pid]) {
                    if (!cols[pid].items.find(x => x.id === u.id)) cols[pid].items.push(u);
                  }
                });
              });
              colEntries = [
                ...(all.items.length ? [['all', all]] : []),
                ...Object.entries(cols).filter(([,c]) => c.items.length > 0 || c.subs.length > 0),
                ...(unassigned.items.length ? [['unassigned', unassigned]] : [])
              ];
            } else {
              // workspace (default flat)
              const cols = {};
              topLevel.forEach(p => { cols[p.id] = { label: p.name, items: [] }; });
              const unassigned = { label: 'Unassigned', items: [] };
              const all = { label: 'All (Super Admin)', items: [] };
              users.forEach(u => {
                if (u.role === 'super_admin') { all.items.push(u); return; }
                const assigned = u.assigned_project_ids || [];
                if (assigned.length === 0) { unassigned.items.push(u); return; }
                const topAssigned = new Set();
                assigned.forEach(pid => { const p = projects.find(x => x.id === pid); if (!p) return; topAssigned.add(p.parent_project_id || p.id); });
                topAssigned.forEach(pid => { if (cols[pid]) cols[pid].items.push(u); });
              });
              colEntries = [...(all.items.length ? [['all', all]] : []), ...Object.entries(cols), ['unassigned', unassigned]].filter(([,c]) => c.items.length > 0);
            }

            const renderUserCard = (u, keyPrefix='') => (
              <div key={u.id + '-' + keyPrefix} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg p-3 group overflow-hidden" data-testid={`admin-board-user-${u.username}`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-7 h-7 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center text-[11px] font-medium">{u.username.charAt(0).toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium break-words">{u.display_name || u.username}</p>
                    <p className="text-[10px] text-zinc-400">@{u.username}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  <Badge className={`${roleColor(u.role)} text-[9px] px-1.5`}>{u.role}</Badge>
                  {!u.is_active && <Badge variant="outline" className="text-[9px] px-1">Inactive</Badge>}
                </div>
                {u.role !== 'super_admin' && (
                  <div className="flex gap-1 mt-2 opacity-0 group-hover:opacity-100">
                    {me?.role === 'super_admin' && <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => viewAs(u.id)}><Eye className="h-3 w-3 mr-0.5" />View</Button>}
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => openAssign(u)}><Settings className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => setResetTarget(u)}><Key className="h-3 w-3" /></Button>
                  </div>
                )}
              </div>
            );

            return (
              <div className="flex gap-3 overflow-x-auto pb-4" data-testid="admin-board">
                {colEntries.map(([k, col]) => (
                  <div key={k} className="flex-shrink-0 w-72 md:w-0 md:flex-1 md:min-w-[240px] md:max-w-sm flex flex-col rounded-xl bg-zinc-50 dark:bg-zinc-900/50 overflow-hidden" data-testid={`admin-col-${k}`}>
                    <div className="flex items-center gap-2 p-3 border-b border-zinc-200 dark:border-zinc-800">
                      <span className="text-xs font-bold uppercase tracking-wider truncate">{col.label}</span>
                      <span className="text-xs text-zinc-400 ml-auto">{col.items.length + (col.subs?.reduce((n,s)=>n+s.items.length,0) || 0)}</span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-2 space-y-2">
                      {col.items.map(u => renderUserCard(u, k))}
                      {(col.subs || []).map(sub => (
                        <div key={sub.id} className="pl-2 border-l-2 border-zinc-200 dark:border-zinc-800 space-y-2">
                          <p className="text-[10px] font-medium uppercase tracking-wider text-zinc-500 pt-1">↳ {sub.label}</p>
                          {sub.items.map(u => renderUserCard(u, k + '-' + sub.id))}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })()
       ) : <div className="space-y-2">
          {users.map(u => (
            <div key={u.id} className="flex items-center justify-between p-4 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-xl group" data-testid={`user-row-${u.username}`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="w-10 h-10 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center font-medium text-zinc-600">
                  {u.username.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{u.display_name || u.username}</span>
                    <Badge className={`${roleColor(u.role)} text-[10px]`}>{u.role}</Badge>
                    {!u.is_active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                  </div>
                  <p className="text-xs text-zinc-400">@{u.username}</p>
                  {(u.assigned_project_ids || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {u.assigned_project_ids.map(pid => <Badge key={pid} variant="secondary" className="text-[9px]">{projectMap[pid] || pid.slice(0,6)}</Badge>)}
                    </div>
                  )}
                </div>
              </div>
              {u.role !== 'super_admin' && (
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {me?.role === 'super_admin' && <Button variant="ghost" size="sm" onClick={() => viewAs(u.id)} data-testid={`view-as-${u.username}`}><Eye className="h-3.5 w-3.5 mr-1" /> View as</Button>}
                  <Button variant="ghost" size="sm" onClick={() => openAssign(u)} data-testid={`assign-${u.username}`}><Settings className="h-3.5 w-3.5 mr-1" /> Assign</Button>
                  <Button variant="ghost" size="icon" onClick={() => setResetTarget(u)} data-testid={`reset-pwd-${u.username}`}><Key className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => toggleDeactivate(u.id)} data-testid={`toggle-active-${u.username}`}>{u.is_active ? <UserX className="h-4 w-4 text-amber-500" /> : <UserCheck className="h-4 w-4 text-emerald-500" />}</Button>
                  <Button variant="ghost" size="icon" onClick={() => setConfirmAction({ type: 'delete', user: u })} data-testid={`delete-user-${u.username}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      }

      {/* Create user dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-user-dialog">
          <DialogHeader><DialogTitle>Create User</DialogTitle><DialogDescription>Add a new user and assign workspaces</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Username</Label><Input value={createForm.username} onChange={e => setCreateForm(p => ({ ...p, username: e.target.value }))} data-testid="cu-username" autoFocus /></div>
            <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={createForm.password} onChange={e => setCreateForm(p => ({ ...p, password: e.target.value }))} data-testid="cu-password" /></div>
            <div className="space-y-1.5"><Label>Display Name</Label><Input value={createForm.display_name} onChange={e => setCreateForm(p => ({ ...p, display_name: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={createForm.role} onValueChange={v => setCreateForm(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="cu-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  {me?.role === 'super_admin' && <SelectItem value="admin">Admin</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Assign Workspaces</Label>
              <div className="border rounded-lg p-3 max-h-52 overflow-y-auto space-y-2">
                {topLevel.map(p => (
                  <div key={p.id}>
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                      <Checkbox checked={createForm.assigned_project_ids.includes(p.id)}
                        onCheckedChange={() => setCreateForm(f => ({ ...f, assigned_project_ids: f.assigned_project_ids.includes(p.id) ? f.assigned_project_ids.filter(x => x !== p.id) : [...f.assigned_project_ids, p.id] }))}
                        data-testid={`cu-assign-${p.id}`} />
                      <span className="font-medium">{p.name}</span>
                    </label>
                    {(subsByParent[p.id] || []).map(s => (
                      <label key={s.id} className="flex items-center gap-2 cursor-pointer text-sm pl-6 mt-1">
                        <Checkbox checked={createForm.assigned_project_ids.includes(s.id)}
                          onCheckedChange={() => setCreateForm(f => ({ ...f, assigned_project_ids: f.assigned_project_ids.includes(s.id) ? f.assigned_project_ids.filter(x => x !== s.id) : [...f.assigned_project_ids, s.id] }))}
                          data-testid={`cu-assign-${s.id}`} />
                        <span className="text-zinc-500">{s.name}</span>
                      </label>
                    ))}
                  </div>
                ))}
                {topLevel.length === 0 && <p className="text-xs text-zinc-400">No workspaces available</p>}
              </div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button onClick={createUser} data-testid="cu-save">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignments dialog */}
      <Dialog open={!!assignTarget} onOpenChange={o => !o && setAssignTarget(null)}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle>Assignments for @{assignTarget?.username}</DialogTitle><DialogDescription>Select workspaces this user can access</DialogDescription></DialogHeader>
          <div className="border rounded-lg p-3 max-h-72 overflow-y-auto space-y-2">
            {topLevel.map(p => (
              <div key={p.id}>
                <label className="flex items-center gap-2 cursor-pointer text-sm">
                  <Checkbox checked={assignSelection.includes(p.id)} onCheckedChange={() => toggleAssign(p.id)} data-testid={`assign-box-${p.id}`} />
                  <span className="font-medium">{p.name}</span>
                </label>
                {(subsByParent[p.id] || []).map(s => (
                  <label key={s.id} className="flex items-center gap-2 cursor-pointer text-sm pl-6 mt-1">
                    <Checkbox checked={assignSelection.includes(s.id)} onCheckedChange={() => toggleAssign(s.id)} data-testid={`assign-box-${s.id}`} />
                    <span className="text-zinc-500">{s.name}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAssignTarget(null)}>Cancel</Button><Button onClick={saveAssign} data-testid="save-assignments">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetTarget} onOpenChange={o => !o && setResetTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password for @{resetTarget?.username}</DialogTitle></DialogHeader>
          <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password (min 6 chars)" data-testid="new-password-input" />
          <DialogFooter><Button variant="outline" onClick={() => setResetTarget(null)}>Cancel</Button><Button onClick={resetPassword} data-testid="confirm-reset">Reset</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={confirmAction?.type === 'delete'} onOpenChange={o => !o && setConfirmAction(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete @{confirmAction?.user?.username}?</DialogTitle><DialogDescription>This action is reversible (soft delete).</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setConfirmAction(null)}>Cancel</Button><Button variant="destructive" onClick={() => deleteUser(confirmAction.user.id)} data-testid="confirm-delete">Delete</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
